/* ----------------------------- engagement ------------------------------ */
/*
 * The retention loop: streaks make showing up a habit, kudos make showing up
 * social, and the cosmetics store gives the points somewhere to go. One
 * balance per person per workspace; every earn and spend goes through the
 * ledger so a balance can always be explained.
 */
/** Days of consecutive activity that earn a milestone bonus. */
export const STREAK_MILESTONES = [3, 7, 14, 30, 60, 100, 365];
/**
 * Every way points enter the system, with the amount fixed here rather than
 * at the call site — a reward that differs by code path reads as a bug to the
 * person on the receiving end.
 */
export const POINT_RULES = {
    daily_active: { amount: 5, label: 'Showed up today' },
    streak_milestone: { amount: 25, label: 'Streak milestone' },
    daily_checkin: { amount: 5, label: 'Answered the daily check-in' },
    kudos_received: { amount: 15, label: 'Received an Appy' },
    kudos_given: { amount: 5, label: 'Sent an Appy' },
    game_win: { amount: 10, label: 'Won a game' },
    focus_session: { amount: 10, label: 'Finished a focus session' },
    /** A birthday or work anniversary — earned by turning up in the world, not the app. */
    celebration: { amount: 20, label: 'Your special day' },
    /**
     * Turning up to the coffee you were drawn for. Worth more than a game and
     * less than an Appy: it costs a quarter of an hour, and the point of the
     * points is that meeting a colleague you would not have met is worth more
     * than another lap of the app.
     */
    coffee: { amount: 15, label: 'Had your coffee chat' },
    /** Amount is per challenge, passed by the sweeper; 0 here is a placeholder. */
    challenge_reward: { amount: 0, label: 'Team challenge met' },
};
/*
 * "Appys" is the user-facing name for kudos — the itsappy-branded pat on the
 * back. The code keeps the generic word so the API reads plainly; only copy
 * shown to people says Appy.
 */
/** Appys are precious because they are scarce. */
export const KUDOS_PER_DAY = 5;
export const KUDOS_MAX_LENGTH = 280;
/** The reaction set offered on a kudos card. Free text is not offered. */
export const KUDOS_EMOJI = ['🙌', '🔥', '💎', '🚀', '🫶', '🏆'];
/**
 * Where an Appy came from. `direct` is the give-an-Appy form; `reaction` is
 * the workspace's designated reaction emoji landing on somebody's message,
 * which is the same Appy with the message as its note.
 */
export const KUDOS_SOURCES = ['direct', 'reaction'];
/**
 * What a workspace is offered when it switches reaction Appys on. Not the
 * default — the feature is off until an owner picks something — but the
 * trophy is what the rest of the suite already uses for a win.
 */
export const KUDOS_SUGGESTED_EMOJI = '🏆';
/** A focus session shorter than this earns nothing — it wasn't focus. */
export const FOCUS_MIN_MINUTES = 10;
export const FOCUS_MAX_MINUTES = 120;
/** Sessions per day that earn points. More are welcome, just unpaid. */
export const FOCUS_PAID_SESSIONS_PER_DAY = 3;
/**
 * How a focus session came to exist.
 *
 * The distinction is not cosmetic: a session somebody started themselves is
 * theirs to end, and walking out of a focus pod on the virtual floor must not
 * end it. Only a session the floor started is the floor's to stop.
 */
export const FOCUS_SOURCES = ['manual', 'focus_pod'];
/**
 * A streak freeze silently covers one missed day. Bought, not given, and
 * capped so a wallet of them never makes the streak meaningless.
 */
export const STREAK_FREEZE_PRICE = 150;
export const MAX_STREAK_FREEZES = 3;
/**
 * The store shelf. Deliberately a fixed catalogue rather than admin-authored
 * content: prices are game balance, and game balance is product's to tune.
 */
export const COSMETIC_CATALOGUE = [
    { id: 'title_rising_star', kind: 'title', label: 'Rising Star', value: 'Rising Star', price: 100 },
    { id: 'title_team_player', kind: 'title', label: 'Team Player', value: 'Team Player', price: 150 },
    { id: 'title_deep_worker', kind: 'title', label: 'Deep Worker', value: 'Deep Worker', price: 200 },
    { id: 'title_hype_captain', kind: 'title', label: 'Hype Captain', value: 'Hype Captain', price: 250 },
    { id: 'title_legend', kind: 'title', label: 'Legend', value: 'Legend', price: 500 },
    { id: 'ring_gold', kind: 'avatar_ring', label: 'Gold ring', value: '#f59e0b', price: 200 },
    { id: 'ring_emerald', kind: 'avatar_ring', label: 'Emerald ring', value: '#10b981', price: 200 },
    { id: 'ring_violet', kind: 'avatar_ring', label: 'Violet ring', value: '#8b5cf6', price: 200 },
    { id: 'ring_rose', kind: 'avatar_ring', label: 'Rose ring', value: '#f43f5e', price: 200 },
    { id: 'ring_prism', kind: 'avatar_ring', label: 'Prism ring', value: 'conic-gradient(#f43f5e,#f59e0b,#10b981,#3b82f6,#8b5cf6,#f43f5e)', price: 400 },
];
export const cosmeticById = (id) => COSMETIC_CATALOGUE.find((item) => item.id === id);
/* -------------------------------- levels -------------------------------- */
/**
 * Levels are pure arithmetic over lifetime points — no stored state, so they
 * can never disagree with the ledger. Names climb from workplace-plain to
 * slightly absurd on purpose; the last one should feel far away.
 */
export const LEVELS = [
    { level: 1, name: 'Newcomer', at: 0 },
    { level: 2, name: 'Regular', at: 100 },
    { level: 3, name: 'Contributor', at: 300 },
    { level: 4, name: 'Achiever', at: 700 },
    { level: 5, name: 'Star', at: 1500 },
    { level: 6, name: 'Luminary', at: 3000 },
    { level: 7, name: 'Mythic', at: 6000 },
];
export function levelFor(lifetimePoints) {
    let current = LEVELS[0];
    for (const entry of LEVELS) {
        if (lifetimePoints >= entry.at)
            current = entry;
    }
    const next = LEVELS.find((entry) => entry.at > current.at);
    return {
        level: current.level,
        name: current.name,
        at: current.at,
        nextAt: next?.at,
        progress: next
            ? Math.min(1, (lifetimePoints - current.at) / (next.at - current.at))
            : 1,
    };
}
/* ----------------------------- achievements ----------------------------- */
/**
 * One-time badges. Everything here is checkable from the ledger and the
 * profile alone — an achievement that needs its own bookkeeping is one that
 * can drift, so those are deliberately not offered.
 */
export const ACHIEVEMENTS = [
    { id: 'first_steps', label: 'First steps', hint: 'Show up for the first time', emoji: '👋' },
    { id: 'streak_7', label: 'On fire', hint: 'Hold a 7-day streak', emoji: '🔥' },
    { id: 'streak_30', label: 'Unstoppable', hint: 'Hold a 30-day streak', emoji: '🌋' },
    { id: 'first_appy_sent', label: 'The hype begins', hint: 'Send your first Appy', emoji: '📣' },
    { id: 'appy_magnet', label: 'Appy magnet', hint: 'Receive 10 Appys', emoji: '🧲' },
    { id: 'serial_hyper', label: 'Serial hyper', hint: 'Send 25 Appys', emoji: '💌' },
    { id: 'first_win', label: 'First win', hint: 'Win a game', emoji: '🏁' },
    { id: 'grandmaster', label: 'Grandmaster', hint: 'Win 10 games', emoji: '♟️' },
    { id: 'deep_diver', label: 'Deep diver', hint: 'Finish 20 focus sessions', emoji: '🤿' },
    { id: 'devoted', label: 'Devoted', hint: 'Answer 10 daily check-ins', emoji: '📋' },
    { id: 'collector', label: 'Collector', hint: 'Own 5 things from the store', emoji: '🎩' },
];
export const achievementById = (id) => ACHIEVEMENTS.find((entry) => entry.id === id);
/* --------------------------- team challenges ---------------------------- */
/**
 * A goal the whole workspace pushes on together, set by an admin: "send 50
 * Appys this week". Progress is computed from what already gets recorded, so
 * a challenge costs nothing to track; meeting it pays every member a bonus.
 */
export const CHALLENGE_METRICS = {
    appys: { label: 'Appys sent', unit: 'Appys' },
    focus_minutes: { label: 'Focus minutes', unit: 'minutes' },
    active_days: { label: 'Active member-days', unit: 'member-days' },
    games: { label: 'Games finished', unit: 'games' },
};
export const CHALLENGE_MAX_REWARD = 100;
export const CHALLENGE_MAX_DAYS = 31;
/* -------------------------------- recap --------------------------------- */
/*
 * "Your week": one person's seven days, shaped for a card they would actually
 * post. Everything here is derived from records the suite already keeps, so a
 * recap costs nothing to store and can always be explained line by line.
 */
/** Shape of an ISO week id, `2026-W05`. */
export const ISO_WEEK_PATTERN = /^\d{4}-W\d{2}$/;
/**
 * The ISO-8601 week id a UTC calendar date falls in.
 *
 * Weeks are addressed by id rather than by date so that a link to somebody's
 * week means the same thing forever, and so the card's back/forward arrows
 * are string arithmetic rather than another date library. The year in the id
 * is the *ISO* year, which is not always the calendar year: 1 January 2027
 * belongs to `2026-W53`.
 */
export function isoWeekId(date) {
    // A week belongs to the year containing its Thursday — that is the whole
    // ISO rule, and shifting to that Thursday first is what makes the rest of
    // this arithmetic rather than special cases.
    const thursday = new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()));
    thursday.setUTCDate(thursday.getUTCDate() - ((thursday.getUTCDay() + 6) % 7) + 3);
    const firstThursday = new Date(Date.UTC(thursday.getUTCFullYear(), 0, 4));
    firstThursday.setUTCDate(firstThursday.getUTCDate() - ((firstThursday.getUTCDay() + 6) % 7) + 3);
    const week = 1 + Math.round((thursday.getTime() - firstThursday.getTime()) / (7 * 86_400_000));
    return `${thursday.getUTCFullYear()}-W${String(week).padStart(2, '0')}`;
}
/**
 * The Monday an ISO week id starts on, as `YYYY-MM-DD`, or null when the id
 * is malformed or names a week the year does not have (there is no W53 most
 * years). Callers turn the plain date into an instant in whatever zone the
 * week is being read in; this stays a calendar answer on purpose.
 */
export function isoWeekStartDay(week) {
    if (!ISO_WEEK_PATTERN.test(week))
        return null;
    const year = Number(week.slice(0, 4));
    const number = Number(week.slice(6));
    if (number < 1 || number > 53)
        return null;
    // Week 1 is the one containing 4 January, so its Monday is that date pulled
    // back to the start of its own week.
    const jan4 = new Date(Date.UTC(year, 0, 4));
    const firstMonday = new Date(jan4);
    firstMonday.setUTCDate(jan4.getUTCDate() - ((jan4.getUTCDay() + 6) % 7));
    const monday = new Date(firstMonday.getTime() + (number - 1) * 7 * 86_400_000);
    // A 53rd week that rolled into the next ISO year was never real.
    if (isoWeekId(monday) !== week)
        return null;
    return monday.toISOString().slice(0, 10);
}
/** `2026-W05` shifted by whole weeks. Negative goes back. */
export function shiftIsoWeek(week, by) {
    const start = isoWeekStartDay(week);
    if (!start)
        return week;
    return isoWeekId(new Date(Date.parse(`${start}T00:00:00Z`) + by * 7 * 86_400_000));
}
const plural = (n, one, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;
/**
 * The one line the card leads with.
 *
 * Rules, never a model: a recap is generated for everyone every Friday, and a
 * sentence that costs tokens, varies run to run and can hallucinate a number
 * is the wrong thing to put at the top of a card people screenshot. The AI
 * blurb is a separate, optional garnish further down.
 *
 * The order below is the editorial judgement — rarest and most flattering
 * first — and the first rule that fires wins, so exactly one thing gets to be
 * the headline.
 */
export function recapHighlight(recap, previous) {
    // Nothing happened. Say so kindly rather than dressing a zero up as news.
    if (recap.points === 0 && recap.daysActive === 0 && recap.messagesSent === 0) {
        return 'A quiet week — nothing to beat next week, which is its own kind of head start.';
    }
    // A badge is the rarest thing that can land on a card, so it wins outright.
    const [badge] = recap.achievementsUnlocked;
    if (badge) {
        const extra = recap.achievementsUnlocked.length - 1;
        return extra > 0
            ? `You unlocked ${badge.label}, and ${plural(extra, 'badge')} more besides.`
            : `You unlocked ${badge.label} this week.`;
    }
    if (recap.rank && recap.rank.position === 1 && recap.rank.of > 1) {
        return `You topped the workspace this week — ${plural(recap.points, 'point')}, ahead of everyone.`;
    }
    // "Best week yet" only means something against a week that actually
    // happened, so a first-ever recap never claims it.
    if (previous && previous.points > 0 && recap.pointsDelta > 0) {
        const growth = recap.pointsDelta / previous.points;
        if (growth >= 0.5) {
            return `${plural(recap.points, 'point')} — up ${recap.pointsDelta} on last week, your strongest in a while.`;
        }
    }
    if (recap.streakDays >= 7) {
        return `${plural(recap.streakDays, 'day')} in a row and counting. Showing up is the whole trick.`;
    }
    if (recap.focusMinutes >= 120) {
        return `${plural(recap.focusMinutes, 'minute')} of deep work across ${plural(recap.focusSessions, 'session')}.`;
    }
    if (recap.kudosReceived >= 3) {
        const from = recap.topSenders[0];
        return from
            ? `${plural(recap.kudosReceived, 'Appy')} came your way, ${from.name} loudest of all.`
            : `${plural(recap.kudosReceived, 'Appy')} came your way this week.`;
    }
    if (recap.tasksCompleted >= 5) {
        return `${plural(recap.tasksCompleted, 'task')} finished. The board is lighter for it.`;
    }
    if (recap.kudosGiven >= 3) {
        return `You handed out ${plural(recap.kudosGiven, 'Appy')}. Somebody's week is better for it.`;
    }
    if (recap.gamesWon > 0) {
        return `${plural(recap.gamesWon, 'win')} at the games table, out of ${recap.gamesPlayed} played.`;
    }
    // The fallback still has to say something true and specific.
    return recap.daysActive > 0
        ? `${plural(recap.points, 'point')} across ${plural(recap.daysActive, 'day')}.`
        : `${plural(recap.points, 'point')} this week.`;
}
